package nl.volleyplanner.web.component

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.datetime.DateTimePeriod
import kotlinx.html.FlowContent
import kotlinx.html.div
import kotlinx.html.h5
import kotlinx.html.h6
import kotlinx.html.hr
import kotlinx.html.span
import kotlinx.html.style
import nl.volleyplanner.api.PersonActivity
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.component.ModalComponent
import nl.volleyplanner.web.base.component.badge
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.util.BootstrapColor
import kotlinx.datetime.LocalDate
import kotlinx.html.classes
import kotlinx.html.em
import nl.volleyplanner.api.entities.FullPerson
import nl.volleyplanner.api.GroupType
import nl.volleyplanner.api.planner.TaskStatistics
import nl.volleyplanner.extensions.DEFAULT_TIMEZONE
import nl.volleyplanner.extensions.compareTo
import nl.volleyplanner.extensions.durationUntil
import nl.volleyplanner.web.DATE_TEXT_SHORT_FORMAT
import nl.volleyplanner.web.LOCAL_TIME_TIME_FORMAT
import nl.volleyplanner.web.base.component.ModalSize
import nl.volleyplanner.web.base.simpleComponent


data class PersonActivitiesState(
    val person: FullPerson,
    val activities: List<PersonActivity>,
    val availableTaskStatistics: Map<String, TaskStatistics> = emptyMap()
)

class PersonScheduleModal(): ModalComponent(
    "Overzicht",
    ModalSize.Large
) {
    private val personActivitiesState = MutableStateFlow<PersonActivitiesState?>(null)

    fun setPerson(person: FullPerson, startDate: LocalDate, endDate: LocalDate, problemVersionSlug: String?, availableTaskStatistics: Map<String, TaskStatistics>) {
        personActivitiesState.value = null
        launchAsync {
            VolleyPlannerApplication.volleyplannerApiClient.organisation
                .persons.getPersonSchedule(person.id, startDate, endDate, problemVersionSlug)
        }.onComplete {
            personActivitiesState.value = PersonActivitiesState(person, it, availableTaskStatistics)
        }
    }

    override fun FlowContent.renderBody() {
        if (personActivitiesState.value == null) {
            spinner()
        } else {
            val state = personActivitiesState.value!!

            // Person Overview Section
            div (classes = "d-flex flex-column") {
                style = "height: 80vh;"
                div(classes = "mb-4") {
                    h5(classes = "mb-2") {
                        +state.person.name
                    }
                    div {
                        // Team groups badges
                        state.person.groups?.get(GroupType.Team.paramValue)?.forEach { team ->
                            badge(color = BootstrapColor.Info, small = true) {
                                classes += "me-1 mb-1"
                                +team.name
                            }
                        }

                        // Referee code badge
                        state.person.groups?.get(GroupType.Scheidsrechters.paramValue)?.firstOrNull()?.let { referee ->
                            badge(color = BootstrapColor.Primary, small = true) {
                                classes += "ms-2 mb-1"
                                +referee.name
                            }
                        }
                    }

                    // Volunteer jobs
                    if (state.person.volunteerJobs?.isNotEmpty() ?: false) {
                        div {
                            state.person.volunteerJobs!!.forEach { volunteerJob ->
                                badge(color = BootstrapColor.Warning, small = true) {
                                    classes += "me-1"
                                    +volunteerJob.jobName
                                }
                            }
                        }
                    }
                }

                hr()

                // Schedule Section
                div (classes = "flex-grow-1 overflow-auto") {
                    if (state.activities.isEmpty()) {
                        div(classes = "text-center text-muted py-4") {
                            +"Geen activiteiten gepland"
                        }
                    } else {
                        // Group activities by date for better overview
                        val activitiesByDate = state.activities
                            .sortedBy { it.startDateTime }
                            .groupBy { it.startDateTime?.date }

                        activitiesByDate.forEach { (date, dayActivities) ->
                            div(classes = "mb-4 border-bottom") {
                                // Compact date header
                                div {
                                    h6 {
                                        +(date?.let { DATE_TEXT_SHORT_FORMAT.format(it) } ?: "Nog in te plannen")
                                    }
                                }

                                // Activities for this day
                                div (classes = "border-start border-dark") {
                                    val sortedActivities = dayActivities.sortedBy { it.startDateTime }

                                    sortedActivities.forEachIndexed { index, activity ->
                                        // Activity card
                                        div(classes = "d-flex") {
                                            if (index < sortedActivities.size - 1) {
                                                classes += "mb-1"
                                            } else {
                                                classes += "mb-3"
                                            }
                                            // Time bar
                                            div(classes = "flex-shrink-0 me-3") {
                                                div(classes = "d-flex flex-column align-items-center text-center") {
                                                    style = "min-width: 60px;"

                                                    // Start time
                                                    div(classes = "fw-bold text-nowrap") {
                                                        style = "font-size: 0.85rem;"
                                                        val startTimeText = activity.startDateTime?.let {
                                                            LOCAL_TIME_TIME_FORMAT.format(it.time)
                                                        } ?: "?"
                                                        +startTimeText
                                                    }

                                                    // Vertical bar representing duration
                                                    div(classes = "bg-${getActivityTypeColor(activity.activityType)} my-1") {
                                                        val durationMinutes =
                                                            if (activity.startDateTime != null && activity.endDateTime != null) {
                                                                activity.startDateTime!!.durationUntil(
                                                                    activity.endDateTime!!,
                                                                    DEFAULT_TIMEZONE
                                                                ).minutes
                                                            } else 60
                                                        val barHeight =
                                                            maxOf(20, minOf(100, (durationMinutes * 0.8).toInt()))
                                                        style = "width: 3px; height: ${barHeight}px;"
                                                    }

                                                    // End time
                                                    div(classes = "fw-bold text-nowrap") {
                                                        style = "font-size: 0.85rem;"
                                                        val endTimeText =
                                                            activity.endDateTime?.let { LOCAL_TIME_TIME_FORMAT.format(it.time) }
                                                                ?: "?"
                                                        +endTimeText
                                                    }
                                                }
                                            }

                                            // Activity content - aligned with time bar center
                                            div(classes = "flex-grow-1 d-flex align-items-center") {
                                                div(classes = "card border-0 w-100") {
                                                    style =
                                                        "box-shadow: 0 1px 3px rgba(0,0,0,0.1); border-left: 3px solid var(--bs-${
                                                            getActivityTypeColor(activity.activityType)
                                                        }) !important;"
                                                    div(classes = "card-body py-2 px-3") {
                                                        div(classes = "row align-items-center") {
                                                            div(classes = "col-auto") {
                                                                span(
                                                                    classes = "badge text-bg-${
                                                                        getActivityTypeColor(
                                                                            activity.activityType
                                                                        )
                                                                    }"
                                                                ) {
                                                                    +activity.activityType
                                                                }
                                                            }
                                                            div(classes = "col") {
                                                                div {
                                                                    +activity.description

                                                                    if (activity.activityType.lowercase().startsWith("taak")) {
                                                                        state.availableTaskStatistics[activity.activityContext.getValue("task_id")]?.let { taskStatistics ->
                                                                            simpleComponent(TaskOnlyStatisticsScoreBadge(taskStatistics))
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }

                                        // Waiting time between activities
                                        if (index < sortedActivities.size - 1) {
                                            val currentActivity = sortedActivities[index]
                                            val nextActivity = sortedActivities[index + 1]

                                            if (currentActivity.endDateTime != null && nextActivity.startDateTime != null) {
                                                val waitingTime = currentActivity.endDateTime!!.durationUntil(
                                                    nextActivity.startDateTime!!,
                                                    DEFAULT_TIMEZONE
                                                )

                                                if (waitingTime.compareTo(DateTimePeriod()) > 0) {
                                                    div(classes = "d-flex mb-1") {
                                                        // Empty space for time bar alignment
                                                        div(classes = "flex-shrink-0 me-3") {
                                                            style = "width: 60px;"
                                                        }

                                                        // Waiting time indicator
                                                        div(classes = "flex-grow-1 d-flex align-items-center") {
                                                            div(classes = "text-muted small bg-light px-3 py-1 border border-secondary-subtle rounded-2") {
                                                                em {
                                                                    +formatWaitingTime(waitingTime)
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    private fun getActivityTypeColor(activityType: String): String {
        // TODO: Can do much better. But also need much better typing structure in the API
        return when (activityType.lowercase()) {
            "thuiswedstrijd" -> "primary"
            "uitwedstrijd" -> "secondary"
            else -> "warning"
        }
    }

    private fun formatWaitingTime(waitingTime: DateTimePeriod): String {
        return when {
            waitingTime.hours == 0 -> "${waitingTime.minutes}m wachten"
            else -> "${waitingTime.hours}u ${waitingTime.minutes}m wachten"
        }
    }

    override suspend fun getState() = personActivitiesState.asStateFlow()
}